import { legacyBlogTarget } from "@/lib/legacy-blog-paths";
import { legacyPageRedirect } from "@/lib/legacy-page-redirects";
import { legacyResultsRedirect } from "@/lib/legacy-results-slugs";
import { LEGACY_UPLOAD_REDIRECTS } from "@/lib/legacy-uploads";

// Blog post HTML in Supabase came from WordPress. Its internal links point at
// www.restorehair.com, old WordPress slugs, clone-of-* duplicates, /blog/...
// paths, double slashes and paths without the trailing slash. Each one costs
// the reader and the crawler one or two redirects. This rewrites each
// internal href and src to the final URL when the page renders. The stored
// HTML does not change.

// Also matches the "htps:" typo found in one post.
const SITE_HOST = /^(?:h?t*ps?:)?\/\/(?:www\.|lp\.)?restorehair\.com(?=[/?#]|$)/i;

// Paths that a dedicated route file redirects. The maps above do not know
// them. Keep each entry the same as its route file.
const ROUTE_REDIRECTS: Record<string, string> = {
  "/ever-hair/": "/non-surgical/", // ever-hair.tsx
  "/laser-therapy-for-hair-loss/": "/low-level-laser-therapy-for-hair-loss/", // laser-therapy-for-hair-loss.tsx
  "/will-i-go-bald-calculator/": "/will-i-go-bald/", // will-i-go-bald-calculator.tsx
  "/hair-cloning-follicle-banking/": "/hair-restoration-research/", // hair-cloning-follicle-banking.tsx
  "/norwood/": "/what-is-the-norwood-scale/", // norwood.tsx
  // Redirect-only route files, added 3 Nov 2026. redirect-link-cleanup.test.ts
  // checks that this map knows every one of them.
  "/baldness-gene/": "/baldness-gene-come-mother-father/", // baldness-gene.tsx
  "/blog/category/brian-urlacher/": "/results/brian-urlacher/", // blog.category.brian-urlacher.tsx
  "/blog/tag/brian-urlacher/": "/results/brian-urlacher/", // blog.tag.brian-urlacher.tsx
  "/consumer-rights/": "/consumer-rights-request/", // consumer-rights.tsx
  "/cookie-policy/": "/cookies/", // cookie-policy.tsx
  "/danny-kanell/": "/results/", // danny-kanell.tsx
  "/denver-post-ops/": "/denver-post-op/", // denver-post-ops.tsx
  "/doctors/dr-bruce-marko/": "/doctors/", // doctors.dr-bruce-marko.tsx
  "/doctors/dr-fahad-juboorib/": "/doctors/dr-fahad-juboori/", // doctors.dr-fahad-juboorib.tsx
  "/doctors/dr-glen-ross/": "/doctors/", // doctors.dr-glen-ross.tsx
  "/doctors/dr-nate-wolkenfeld/": "/doctors/", // doctors.dr-nate-wolkenfeld.tsx
  "/doctors/dr-talha-ahmed/": "/doctors/", // doctors.dr-talha-ahmed.tsx
  "/formidable-test-page-1/": "/", // formidable-test-page-1.tsx
  "/hair-transplant-price/": "/pricing/", // hair-transplant-price.tsx
  "/location/milwaukee/": "/locations/wisconsin/milwaukee/", // location.milwaukee.tsx
  "/location/paramus/": "/locations/new-jersey/paramus/", // location.paramus.tsx
  "/locations/colorado/downtown-denver/": "/locations/restore-denver/", // locations.colorado.downtown-denver.tsx
  "/locations/colorado/greenwood-village/": "/locations/restore-denver/", // locations.colorado.greenwood-village.tsx
  "/locations/district-of-columbia/washington-dc/": "/locations/district-of-columbia/", // locations.district-of-columbia.washington-dc.tsx
  "/locations/illinois/chicago-fulton-market/": "/locations/restore-chicago/", // locations.illinois.chicago-fulton-market.tsx
  "/locations/illinois/chicago-hancock-center/": "/locations/restore-chicago/", // locations.illinois.chicago-hancock-center.tsx
  "/locations/illinois/chicago-lincoln-park/": "/locations/restore-chicago/", // locations.illinois.chicago-lincoln-park.tsx
  "/locations/illinois/deerfield/": "/locations/restore-chicago/", // locations.illinois.deerfield.tsx
  "/locations/illinois/lake-forest/": "/locations/restore-chicago/", // locations.illinois.lake-forest.tsx
  "/locations/illinois/oak-brook/": "/locations/restore-chicago/", // locations.illinois.oak-brook.tsx
  "/locations/illinois/orland-park/": "/locations/restore-chicago/", // locations.illinois.orland-park.tsx
  "/locations/illinois/schaumburg/": "/locations/restore-chicago/", // locations.illinois.schaumburg.tsx
  "/locations/illinois/st-charles/": "/locations/restore-chicago/", // locations.illinois.st-charles.tsx
  "/locations/new-york/manhasset/": "/locations/restore-northeast/", // locations.new-york.manhasset.tsx
  "/locations/new-york/manhattan/": "/locations/restore-northeast/", // locations.new-york.manhattan.tsx
  "/locations/new-york/melville/": "/locations/restore-northeast/", // locations.new-york.melville.tsx
  "/locations/north-carolina/charlotte/": "/locations/restore-charlotte/", // locations.north-carolina.charlotte.tsx
  "/locations/north-carolina/cornelius/": "/locations/restore-charlotte/", // locations.north-carolina.cornelius.tsx
  "/locations/restore-national/": "/locations/restore-northeast/", // locations.restore-national.tsx
  "/locations/texas/arlington/": "/locations/restore-dallas/", // locations.texas.arlington.tsx
  "/locations/texas/dallas-plano/": "/locations/restore-dallas/", // locations.texas.dallas-plano.tsx
  "/locations/washington/bellevue/": "/locations/", // locations.washington.bellevue.tsx
  "/national-west/": "/locations/restore-west/", // national-west.tsx
  "/quick-quote-norwood-page-1/": "/quick-quote-part-1/", // quick-quote-norwood-page-1.tsx
  "/restore-charlotte/": "/locations/restore-charlotte/", // restore-charlotte.tsx
  "/restore-chicago/": "/locations/restore-chicago/", // restore-chicago.tsx
  "/restore-dallas/": "/locations/restore-dallas/", // restore-dallas.tsx
  "/restore-midwest/": "/locations/restore-midwest/", // restore-midwest.tsx
  "/restore-national/": "/locations/restore-northeast/", // restore-national.tsx
  "/restore-south/": "/locations/restore-south/", // restore-south.tsx
  "/restore-west/": "/locations/restore-west/", // restore-west.tsx
  "/results/urlacher/": "/results/brian-urlacher/", // results.urlacher.tsx
  "/schedule-a-consult/brooklyn/": "/schedule-a-consult/", // schedule-a-consult.brooklyn.tsx
  "/schedule-a-consult/highland-park-place/": "/schedule-a-consult/", // schedule-a-consult.highland-park-place.tsx
  "/schedule-a-consult/south-lake/": "/schedule-a-consult/", // schedule-a-consult.south-lake.tsx
  "/why-restore/meet-the-doctor/": "/doctors/dr-james-harris/", // why-restore_.meet-the-doctor.tsx
  // FAQ_PAGE_REDIRECTS in faq-data.tsx (faq.$slug.tsx)
  "/faq/how-much-does-a-hair-transplant-cost/": "/the-true-hair-restoration-cost/",
  "/faq/what-is-the-norwood-scale-used-to-diagnose-hair-loss/": "/what-is-the-norwood-scale/",
  "/faq/where-are-you-located/": "/locations/",
  "/faq/does-the-restore-no-shave-fue-method-really-work/": "/no-shave-fue-hair-transplant/",
  "/faq/is-sedation-required-for-the-procedure/": "/faq/will-i-be-awake-during-a-hair-transplant/",
};

/** The final path for an internal path, without the query string. */
export function finalInternalPath(rawPath: string): string {
  // Slugs never contain spaces. Some imported hrefs do ("/ever-hair/ /").
  const cleaned = rawPath.replace(/(?:%20|\s)+/g, "");
  const path = `/${cleaned.replace(/^\/+/, "")}`.replace(/\/{2,}/g, "/");
  if (path === "/") return path;
  if (path.startsWith("/wp-content/")) {
    return LEGACY_UPLOAD_REDIRECTS[path.slice("/wp-content/".length)] ?? path;
  }
  const parts = path.split("/").filter(Boolean);
  const last = parts[parts.length - 1];
  // A file (image, PDF): keep it as it is.
  if (last.includes(".")) return path;
  const routeTarget = ROUTE_REDIRECTS[`/${parts.join("/")}/`];
  if (routeTarget) return routeTarget;
  if (parts[0] === "blog" && parts.length > 1) return legacyBlogTarget(parts.slice(1).join("/"));
  if (parts.length === 1) {
    const slug = parts[0];
    return legacyResultsRedirect(slug) ?? legacyPageRedirect(slug) ?? `/${slug}/`;
  }
  return `/${parts.join("/")}/`;
}

function rewriteUrl(url: string): string {
  let rest: string;
  if (SITE_HOST.test(url)) rest = url.replace(SITE_HOST, "");
  else if (url.startsWith("/") && !url.startsWith("//")) rest = url;
  else return url;
  const match = /^([^?#]*)(.*)$/.exec(rest);
  const pathPart = match?.[1] ?? "";
  const suffix = match?.[2] ?? "";
  return finalInternalPath(pathPart || "/") + suffix;
}

/** Point every internal href and src in post HTML at its final URL. */
export function rewriteInternalLinks(html: string): string {
  return html.replace(
    /\b(href|src)=("|')([^"']*)\2/gi,
    (_m, attr: string, quote: string, url: string) => `${attr}=${quote}${rewriteUrl(url)}${quote}`,
  );
}

/**
 * The post page already renders the post title as its one <h1>. Some
 * imported post bodies also contain an <h1>, so the page had two. Turn every
 * <h1> in the body into an <h2>.
 */
export function demoteHeadings(html: string): string {
  return html.replace(/<(\/?)h1(?=[\s>])/gi, "<$1h2");
}
